<template>
  <div v-loading="loading" class="work-detail-body">
    <div class="body-preview">
      <template v-for="(item, index) in previewData" :key="index">
        <div class="body-preview-item" :class="{ 'is-first': index === 0 }">
          <div class="avatar">
            <img alt="" :src="getPreviewAvatar(item)" />
          </div>
          <div class="desc">
            <div class="title">{{ item.title }}</div>
            <div class="num">{{ item.num }}</div>
          </div>
        </div>
      </template>
    </div>
    <div class="body-content">
      <van-list
        v-if="dataList.length > 0"
        v-model:loading="listLoading"
        :finished="finished"
        finished-text="没有更多了"
        :offset="10"
        @load="onLoad"
      >
        <template v-for="(item, index) in dataList" :key="index">
          <div
            v-if="
              index === 0 ||
              timeFormat(item.updateTime) !== timeFormat(dataList[index - 1].updateTime)
            "
            class="body-content-item__time"
          >
            {{ timeFormat(item.updateTime) }}
          </div>
          <div class="body-content-item__main">
            <div class="body-content-item__main-left">
              <img alt="" :src="getPreviewAvatar(item)" />
              <div class="time-line"></div>
            </div>
            <div class="body-content-item__main-right">
              <div class="top">
                <div>{{ item.title }}</div>
              </div>
              <template v-for="desc in item.detailList" :key="desc.trainMainId">
                <div class="desc">
                  <div class="status" :class="{ 'status--success': desc.status === 2 }"></div>
                  <div class="time">
                    {{ desc.timeStamp
                    }}<span v-if="desc.crossDay" class="time-subfix">
                      +{{ desc.crossDayCount }}</span
                    >
                  </div>
                  <div class="text">{{ desc.message }}</div>
                </div>
              </template>
            </div>
          </div>
        </template>
      </van-list>
      <Empty v-else empty-text="暂无可查看的工作明细" />
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import xbb from '@xbb/xbb-utils'
import { getWorkProgressDetailList } from '@/api/app-home.js'
import Empty from '@/components/Empty/index.vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
})

const previewData = ref([])
const page = ref(1)
const pageSize = ref(10)
const rowsCount = ref(0)
const loading = ref(false)
const listLoading = ref(false)
const finished = ref(false)
const dataList = ref([])

const timeFormat = (time) => {
  if (!time) return ''
  return xbb.formatTime(time, 'yyyy-MM-dd')
}

const getPreviewAvatar = (item) => {
  const index = item.assistantId === '1' ? '10' : '14'
  return require(`@/assets/ai-avatar/ai-avatar-2x-${index}.png`)
}

const getList = async () => {
  loading.value = page.value === 1
  try {
    const res = await getWorkProgressDetailList({
      page: page.value,
      pageSize: pageSize.value
    })
    if (page.value === 1) {
      previewData.value = []
      if (res.result.meetingLogCount) {
        previewData.value.push({
          assistantId: '1',
          title: '跟进助手',
          num: res.result.meetingLogCount
        })
      }
      if (res.result.trainLogCount) {
        previewData.value.push({
          assistantId: '2',
          title: '陪练助手',
          num: res.result.trainLogCount
        })
      }
    }
    const list = res.result.groupList || []
    rowsCount.value = res.result.pageHelper.rowsCount
    dataList.value = [...dataList.value, ...list]
    page.value++
    finished.value = dataList.value.length >= rowsCount.value
  } catch (e) {
    finished.value = true
  } finally {
    loading.value = false
    listLoading.value = false
  }
}

const onLoad = () => {
  if (page.value === 1) return
  getList()
}

watch(
  () => props.show,
  (val) => {
    if (val) {
      page.value = 1
      previewData.value = []
      dataList.value = []
      finished.value = false
      listLoading.value = false
      getList()
    }
  },
  { immediate: true }
)
</script>

<style lang="scss" scoped>
.work-detail-body {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  .body-preview {
    flex: 0 0 auto;
    margin: 0 12px;
    padding: 12px 0;
    display: flex;
    border-bottom: 1px dashed #f2f3f5;
    .body-preview-item {
      flex: 1;
      margin-left: 12px;
      padding: 10px 16px;
      border-radius: 16px;
      background: linear-gradient(291deg, #eef2ff 1%, #f5f3ff 100%);
      box-sizing: border-box;
      border: 1px solid rgba(124, 58, 237, 0.15);
      display: flex;
      .avatar {
        background: #fff;
        border: 0.83px solid #ffffff;
        box-shadow: 0px 0px 5.33px 0px rgba(56, 47, 220, 0.2);
        margin-right: 16px;
        height: 34px;
        width: 34px;
        border-radius: 50%;
        overflow: hidden;
        img {
          width: 32px;
          height: 32px;
        }
      }
      .desc {
        .title {
          font-size: 13px;
          line-height: 18px;
          color: #646566;
        }
        .num {
          font-size: 18px;
          font-weight: 600;
          line-height: 26px;
          color: #323233;
        }
      }
    }
    .is-first {
      margin-left: 0;
    }
  }
  .body-content {
    flex: 1;
    overflow-y: auto;
    padding: 0 12px 12px 12px;
    .body-content-item__time {
      margin-top: 16px;
      font-size: 14px;
      font-weight: 600;
      line-height: 20px;
      color: #969799;
    }
    .body-content-item__main {
      margin-top: 12px;
      display: flex;
      &-left {
        flex: 0 0 auto;
        width: 40px;
        margin-right: 8px;
        display: flex;
        flex-direction: column;
        align-items: center;
        img {
          height: 24px;
          width: 24px;
          border-radius: 50%;
          box-sizing: border-box;
          border: 0.5px solid #ffffff;

          box-shadow: 0px 0px 4px 0px rgba(56, 47, 220, 0.2);
        }
        .time-line {
          margin-top: 8px;
          width: 1px;
          height: 100%;
          background: #f2f3f5;
        }
      }
      &-right {
        display: flex;
        flex-direction: column;
        .top {
          display: flex;
          align-items: center;
          font-size: 13px;
          font-weight: 600;
          line-height: 18px;
          color: #323233;
          line-height: 24px;
        }
        .desc {
          display: flex;
          margin-top: 8px;

          .status {
            display: inline-block;
            flex: 0 0 auto;
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #f54e4e;
            margin-right: 8px;
            margin-top: 5px;
          }
          .status--success {
            background: #00c241;
          }

          .time {
            flex: 0 0 auto;
            font-size: 13px;
            line-height: 18px;
            color: #969799;
            margin-right: 8px;
            display: flex;
          }
          .time-subfix {
            font-size: 10px;
            line-height: 14px;
            color: #969799;
            margin-left: 2px;
          }
          .text {
            font-size: 13px;
            font-weight: normal;
            line-height: 18px;
            color: #323233;
          }
        }
      }
    }
  }
}
</style>
